import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHistoryStore } from '@/stores/historyStore';
import { useTagsStore } from '@/stores/tagsStore';
import { Button } from '@/components/Button';
import { formatJson, truncateString } from '@/utils/json';
import type { TagType, EconomicSectorRequest, GrammaticalReferenceRequest } from '@/types/api';
import styles from './HistoryPage.module.css';

const TYPE_ROUTES: Record<TagType, string> = {
  thematic: '/thematic',
  economic_sector: '/economic-sector',
  grammatical_reference: '/grammatical-reference',
};

export function HistoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { executions, removeEntry, clearHistory } = useHistoryStore();
  const { loadFromHistory } = useTagsStore();

  const handleLoadIntoForm = useCallback(
    (entry: typeof executions[0]) => {
      const { type, requestPayload } = entry;
      const lessonMarkdown = requestPayload.lesson_markdown;
      let validValues: string[] | undefined;
      let language: string | undefined;

      if (type === 'economic_sector') {
        validValues = (requestPayload as EconomicSectorRequest).valid_values;
      } else if (type === 'grammatical_reference') {
        const grPayload = requestPayload as GrammaticalReferenceRequest;
        validValues = grPayload.valid_values;
        language = grPayload.language;
      }

      loadFromHistory(type, lessonMarkdown, validValues, language as import('@/types/api').LanguageCode);
      navigate(TYPE_ROUTES[type]);
    },
    [loadFromHistory, navigate]
  );

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>{t('pages.history.title')}</h1>
          <p className={styles.description}>{t('pages.history.description')}</p>
        </div>
        {executions.length > 0 && (
          <Button variant="danger" size="small" onClick={clearHistory}>
            {t('history.clearAll')}
          </Button>
        )}
      </header>

      {executions.length === 0 ? (
        <div className={styles.empty}>
          <p>{t('history.empty')}</p>
        </div>
      ) : (
        <div className={styles.list}>
          {executions.map((entry) => (
            <article key={entry.id} className={styles.entry}>
              <div className={styles.entryHeader}>
                <span className={`${styles.type} ${styles[entry.type]}`}>{t(`types.${entry.type}`)}</span>
                <time className={styles.time}>{formatTime(entry.createdAt)}</time>
              </div>

              <div className={styles.entryContent}>
                <div className={styles.preview}>
                  <strong>{t('history.request')}:</strong>
                  <code>{truncateString(entry.requestPayload.lesson_markdown, 100)}</code>
                </div>

                {entry.error ? (
                  <div className={styles.error}>
                    <strong>{t('common.error')}:</strong> {entry.error}
                  </div>
                ) : entry.responsePayload && (
                  <div className={styles.success}>
                    <strong>{t('history.response')}:</strong> {t('history.responseReceived')}
                  </div>
                )}

                {entry.responseHeaders['x-request-id'] && (
                  <div className={styles.traceId}>
                    <strong>{t('result.traceId')}:</strong> <code>{entry.responseHeaders['x-request-id']}</code>
                  </div>
                )}
              </div>

              <details className={styles.details}>
                <summary>{t('debug.title')}</summary>
                <div className={styles.detailsContent}>
                  <div className={styles.jsonSection}>
                    <strong>{t('debug.request')}</strong>
                    <pre>{formatJson(entry.requestPayload)}</pre>
                  </div>
                  {entry.responsePayload && (
                    <div className={styles.jsonSection}>
                      <strong>{t('debug.response')}</strong>
                      <pre>{formatJson(entry.responsePayload)}</pre>
                    </div>
                  )}
                </div>
              </details>

              <div className={styles.entryActions}>
                <Button size="small" onClick={() => handleLoadIntoForm(entry)}>
                  {t('history.loadIntoForm')}
                </Button>
                <Button size="small" variant="ghost" onClick={() => removeEntry(entry.id)}>
                  {t('common.delete')}
                </Button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
